import React from 'react';
import Navbar from './components/navbar';
import CustomCursor from './components/CustomCursor';
import useTiltEffect from './hooks/useTiltEffect';
import useMagneticEffect from './hooks/useMagneticEffect';
import './App.css'; // Import your CSS file
import { useNavigate } from 'react-router-dom';
import usePageSEO from './hooks/usePageSEO';

function LazyBackgroundVideo() {
  const videoRef = React.useRef(null);
  const [posterSrc, setPosterSrc] = React.useState(() => {
    if (typeof window !== 'undefined' && window.innerWidth <= 620) {
      return 'images/main-background-mobile.webp';
    }
    return 'images/main-background.webp';
  });

  React.useEffect(() => {
    const handleResize = () => {
      const isMobileWidth = window.innerWidth <= 620;
      setPosterSrc(isMobileWidth ? 'images/main-background-mobile.webp' : 'images/main-background.webp');
    };
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const setVideoRef = React.useCallback((el) => {
    videoRef.current = el;
    if (el) {
      el.muted = true;
      el.defaultMuted = true;
      el.playsInline = true;
      el.setAttribute('playsinline', '');
      el.setAttribute('webkit-playsinline', '');
      el.setAttribute('x5-playsinline', '');
    }
  }, []);

  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = true;
    video.defaultMuted = true;

    const playVideo = () => {
      video.muted = true;
      const promise = video.play();
      if (promise !== undefined) {
        promise.catch(() => {
          // On mobile devices with battery saver / Low Power Mode, start on user touch or scroll
          const onUserGesture = () => {
            if (videoRef.current) {
              videoRef.current.muted = true;
              videoRef.current.play().catch(() => {});
            }
          };
          window.addEventListener('touchstart', onUserGesture, { once: true, passive: true });
          window.addEventListener('touchend', onUserGesture, { once: true, passive: true });
          window.addEventListener('scroll', onUserGesture, { once: true, passive: true });
          document.addEventListener('click', onUserGesture, { once: true });
        });
      }
    };

    playVideo();
  }, []);

  return (
    <video
      ref={setVideoRef}
      className="main-bg-img"
      poster={posterSrc}
      src="images/main-bg.webm"
      autoPlay
      loop
      muted
      playsInline
      webkit-playsinline="true"
      playsinline="true"
      preload="auto"
      aria-hidden="true"
      onLoadedMetadata={(e) => {
        e.currentTarget.muted = true;
        e.currentTarget.play().catch(() => {});
      }}
      onCanPlay={(e) => {
        e.currentTarget.muted = true;
        e.currentTarget.play().catch(() => {});
      }}
    >
      <source src="images/main-bg.webm" type="video/webm" />
    </video>
  );
}

function App() {
  const navigate = useNavigate();

  const heroTiltRef = useTiltEffect(1.8);
  const introTiltRef = useTiltEffect(1.5);
  const socialsTiltRef = useTiltEffect(1.8);
  const featTextRef = useTiltEffect(1.5);
  const feat1Ref = useTiltEffect(2.2);
  const feat2Ref = useTiltEffect(2.2);
  const feat3Ref = useTiltEffect(2.2);
  const feat4Ref = useTiltEffect(2.2);
  const heroBtnRef = useMagneticEffect(0.2);

  usePageSEO({
    title: 'Fit Realm | AI-Driven Fitness Tools & Guidance',
    description: 'Fit Realm is your AI-powered fitness companion. Get AI workout and diet plans, fitness calculators, and 24/7 guidance with Fit G.',
    keywords: 'fitness, AI fitness, workout planner, diet plan, fitness tracker, fit g, exercise app, fat loss, muscle gain',
    canonical: 'https://thefitrealm.in/'
  });

  const [isMobile, setIsMobile] = React.useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 1181;
    }
    return false;
  });

  React.useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 1181;
      setIsMobile((prev) => (prev !== mobile ? mobile : prev));
    };
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleNavigate = (path) => {
    localStorage.setItem('redirectPath', path); // Store the intended path
    navigate(path);
  };

  const handleFeatureCardMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    e.currentTarget.style.setProperty('--mouse-x', `${x}px`);
    e.currentTarget.style.setProperty('--mouse-y', `${y}px`);
  };

  const links = [
    { url: '/', image: 'images/home.webp', text: 'Home', active: window.location.pathname === '/' },
    { url: '/fit-g', image: 'images/genie.webp', text: 'Fit-G', active: window.location.pathname === '/fit-g' },
    { url: '/exerciselogs', image: 'images/exercise.webp', text: 'Exercise Logs', active: window.location.pathname === '/exerciselogs' },
    { url: '/calculators/bmi', image: 'images/calculator.webp', text: 'Fitness Calculators', active: window.location.pathname === '/calculators/bmi' },
  ];

  return (
    <div>
      <CustomCursor />
      <main id="main-content" role="main" className="page-route-enter">
        {!isMobile ? (
          <div className='pc-div'>
        <Navbar links={links} />
        <div className="main-container">
          <div className='left-container'>
            <div className="main-content" ref={heroTiltRef}>
              <LazyBackgroundVideo />

              <h1>WANT TO GET <span style={{ color: '#FF3131' }}>FIT</span>?</h1>
              <p>Empowering Your Fitness Journey with Personalized AI Guidance -<span style={{ color: '#FFFFFF' }}> Featuring AI Meal and Exercise Planner</span> </p>
              <button ref={heroBtnRef} onClick={() => handleNavigate('/exerciselogs')}>Get Started</button>
            </div>
            <div className='features-container'>
              <div className='multi-container'>
                <h1 className='features-text' ref={featTextRef}>Features</h1>
                <div className='socials-container' ref={socialsTiltRef}>
                  <h1>Socials</h1>
                  <div style={{ alignContent: 'center' }}>
                    <a href='https://www.instagram.com/the.fitrealm_/' target='_blank' rel='noopener noreferrer'><img src='images/instagram.webp' alt='Instagram - Fit Realm' title='Instagram - Fit Realm' width="30" height="30" loading="lazy" /></a>
                    <a href='https://www.facebook.com/profile.php?id=61577838901247' target='_blank' rel='noopener noreferrer'><img src='images/facebook.webp' alt='Facebook - Fit Realm' title='Facebook - Fit Realm' width="30" height="30" loading="lazy" /></a>
                    <a href='https://www.linkedin.com/company/fit-realm/' target='_blank' rel='noopener noreferrer'><img src='images/linkedin.webp' alt='Linkedin - Fit Realm' title='Linkedin - Fit Realm' width="30" height="30" loading="lazy" /></a>
                    <a href='/blogs'><img src='images/notes.webp' alt='Blogs - Fit Realm' title='Blogs - Fit Realm' width="30" height="30" loading="lazy" /></a>
                  </div>
                </div>
              </div>
              <div className='features-box-container'>
                <div className='features-box' ref={feat1Ref} onMouseMove={handleFeatureCardMouseMove} onClick={() => navigate('/fit-g')} >
                  <div>
                    <h2>Chat with <span style={{ color: '#FF3131' }}>Fit </span>G</h2>
                    <p>Get instant expert fitness, workout, and nutrition answers anytime with your AI fitness companion.</p>
                  </div>
                  <img src='images/genie.webp' alt='Fit G - Fit Realm' title='Talk to Fit G' width="30" height="30" loading="lazy" />
                </div>
                <div className='features-box' ref={feat2Ref} onMouseMove={handleFeatureCardMouseMove}>
                  <div>
                    <h2>AI Meal Planner</h2>
                    <p>Personalized meal plans tailored to your goals: fat loss, muscle gain, or healthy living.</p>
                  </div>
                  <img src='images/diet.webp' alt='Diet Plan - Fit Realm' title='Diet Plan - Fit Realm' width="30" height="30" loading="lazy" />
                </div>
                <div className='features-box' ref={feat3Ref} onMouseMove={handleFeatureCardMouseMove} onClick={() => navigate('/exerciselogs')}>
                  <div>
                    <h2>AI Exercise Planner</h2>
                    <p>Customized workouts crafted by AI to match your goals, body type, and fitness level.</p>
                  </div>
                  <img src='images/exercise.webp' alt='Exercise Plan - Fit Realm' title='Generate Exercise Plans - Fit Realm' width="30" height="30" loading="lazy" />
                </div>
                <div className='features-box' ref={feat4Ref} onMouseMove={handleFeatureCardMouseMove} onClick={() => navigate('/calculators/bmi')}>
                  <div>
                    <h2><span style={{ color: '#FF3131' }}>Fit</span>ness Calculators</h2>
                    <p>Track BMI, calories, macros, and more — precision tools to power your fitness journey.</p>
                  </div>
                  <img src='images/calculator.webp' alt='Fitness Calculators - Fit Realm' title='Fitness Calculators - Fit Realm' width="30" height="30" loading="lazy" />
                </div>
              </div>
            </div>
          </div>
          <div className='intro-container' ref={introTiltRef}>
            <h1>INTRODUCING <span style={{ color: '#ff3131' }}>FIT</span>REALM</h1>

            <p>At <span style={{ color: '#ffffff' }}>Fit Realm</span>, we bring you <span style={{ color: '#ff3131' }}>AI-generated Exercise Plans, customized AI Diet Plans, and smart Food and Exercise Trackers</span> — built around your body, your goals, and your lifestyle.
              <br />Experience <span style={{ color: '#ffffff' }}>real-time Progress Tracking</span>, smarter decisions with <span style={{ color: '#ffffff' }}>Fitness Calculators</span>, and <span style={{ color: '#ffffff' }}>instant expert support</span> whenever you need.
              <br />No generic plans. No guesswork. Only <span style={{ color: '#ff3131' }}>precision fitness guidance, AI-driven meal planning, and results you can trust.</span>
              <br />Whether you're striving for <span style={{ color: '#ffffff' }}>fat loss, muscle building</span>, or simply living healthier, <span style={{ color: '#ffffff' }}>FitRealm gives you everything you need</span> to succeed.
              <br /><span style={{ color: '#ffffff' }}>Train with purpose, fuel intelligently, and transform completely — with Fit Realm leading your way.</span></p>
            <img src='images/about-bg.webp' alt='FitRealm - AI Personal Trainer' loading='lazy' />
          </div>

        </div>
      </div>
        ) : (
      <div className='tablet-div' id='style-1'>
        <div className='tablet-container'>
          <Navbar links={links} />
          <div className="main-content-tablet">
            <LazyBackgroundVideo />
            <h1>WANT TO GET <span style={{ color: '#FF3131' }}>FIT</span>?</h1>
            <p>Empowering Your Fitness Journey with Personalized AI Guidance -<span style={{ color: '#FFFFFF' }}> Featuring AI Meal and Exercise Planner</span></p>
            <button onClick={() => handleNavigate('/exerciselogs')}>Join Us Now  →</button>
          </div>
        </div>

        <div className='tablet-container' >
          <div className='middle-container-tablet'>

            <div className='joined-container-tablet'>
              <div className="intro-container-tablet">
                <h1>INTRODUCING <span style={{ color: '#ff3131' }}>FIT</span>REALM</h1>
                <p>At <span style={{ color: '#ffffff' }}>Fit Realm</span>, we bring you <span style={{ color: '#ff3131' }}>AI-generated Exercise Plans, customized AI Diet Plans, and smart Food and Exercise Trackers</span> — built around your body, your goals, and your lifestyle.
                  <br />Experience <span style={{ color: '#ffffff' }}>real-time Progress Tracking</span>, smarter decisions with <span style={{ color: '#ffffff' }}>Fitness Calculators</span>, and <span style={{ color: '#ffffff' }}>instant expert support</span> whenever you need.
                  <br />No generic plans. No guesswork. Only <span style={{ color: '#ff3131' }}>precision fitness guidance, AI-driven meal planning, and results you can trust.</span>
                  <br />Whether you're striving for <span style={{ color: '#ffffff' }}>fat loss, muscle building</span>, or simply living healthier, <span style={{ color: '#ffffff' }}>FitRealm gives you everything you need</span> to succeed.
                  <br /><span style={{ color: '#ffffff' }}>Train with purpose, fuel intelligently, and transform completely — with Fit Realm leading your way.</span></p>
                <img src='images/about1.webp' alt='FitRealm - AI Gym Trainer' loading='lazy' />
              </div>
              <div className='features-container-tablet'>
                <h1>Features</h1>
                <div className='features-box-tablet' onClick={() => navigate('/fit-g')}>
                  <div>
                    <h2>Chat with <span style={{ color: '#FF3131' }}>Fit</span> G</h2>
                    <p>Chat with our AI assistant, Fit G, to get started with your fitness journey.</p>
                  </div>
                  <img src='images/genie.webp' alt='Fit G by FitRealm' title='Talk to Fit G' width="30" height="30" loading="lazy" />
                </div>
                <div className='features-box-tablet' >
                  <div>
                    <h2>AI Meal Planner</h2>
                    <p>Chat with our AI assistant, Fit G, to get started with your fitness journey.</p>
                  </div>
                  <img src='images/diet.webp' alt='FitRealm - Diet Plan for Weight Loss' width="30" height="30" loading="lazy" />
                </div>
                <div className='features-box-tablet' onClick={() => navigate('/exerciselogs')}>
                  <div>
                    <h2>AI Exercise Planner</h2>
                    <p>Chat with our AI assistant, Fit G, to get started with your fitness journey.</p>
                  </div>
                  <img src='images/exercise.webp' alt='FitRealm - AI Exercise Planner' title='Generate Exercise Plans' width="30" height="30" loading="lazy" />
                </div>
                <div className='features-box-tablet' onClick={() => navigate('/calculators/bmi')}>
                  <div>
                    <h2><span style={{ color: '#FF3131' }}>Fit</span>ness Calculators</h2>
                    <p>Chat with our AI assistant, Fit G, to get started with your fitness journey.</p>
                  </div>
                  <img src='images/calculator.webp' alt='Fitness Calculators by FitRealm' title='Fitness Calculators' width="30" height="30" loading="lazy" />
                </div>
              </div>
            </div>

            <div className='bottom-container-tablet'>
              <h1>OUR <span style={{ color: '#FF3131' }}>SOCIALS</span></h1>
              <hr style={{ width: '40%', border: '2px solid #FF3131' }} />
              <div className='socials-container-tablet'>
                <a href='https://www.instagram.com/the.fitrealm_/' target='_blank' rel='noopener noreferrer'><img src='images/instagram.webp' alt='Instagram - Fit Realm' title='Instagram - Fit Realm' width="30" height="30" loading="lazy" /></a>
                <a href='https://www.facebook.com/profile.php?id=61577838901247' target='_blank' rel='noopener noreferrer'><img src='images/facebook.webp' alt='Facebook - Fit Realm' title='Facebook - Fit Realm' width="30" height="30" loading="lazy" /></a>
                <a href='https://www.linkedin.com/company/fit-realm/' target='_blank' rel='noopener noreferrer'><img src='images/linkedin.webp' alt='Linkedin - Fit Realm' title='Linkedin - Fit Realm' width="30" height="30" loading="lazy" /></a>
                <a href='/blogs'><img src='images/notes.webp' alt='Blogs - Fit Realm' title='Blogs - Fit Realm' width="30" height="30" loading="lazy" /></a>
              </div>
            </div>

          </div>
          <div className="intro-container-mobile">
            <h1>INTRODUCING <span style={{ color: '#ff3131' }}>FIT</span>REALM</h1>
            <p>At <span style={{ color: '#ffffff' }}>Fit Realm</span>, we bring you <span style={{ color: '#ff3131' }}>AI-generated Exercise Plans, customized AI Diet Plans, and smart Food and Exercise Trackers</span> — built around your body, your goals, and your lifestyle.
              <br />Experience <span style={{ color: '#ffffff' }}>real-time Progress Tracking</span>, smarter decisions with <span style={{ color: '#ffffff' }}>Fitness Calculators</span>, and <span style={{ color: '#ffffff' }}>instant expert support</span> whenever you need.
              <br />No generic plans. No guesswork. Only <span style={{ color: '#ff3131' }}>precision fitness guidance, AI-driven meal planning, and results you can trust.</span>
              <br />Whether you're striving for <span style={{ color: '#ffffff' }}>fat loss, muscle building</span>, or simply living healthier, <span style={{ color: '#ffffff' }}>FitRealm gives you everything you need</span> to succeed.
              <br /><span style={{ color: '#ffffff' }}>Train with purpose, fuel intelligently, and transform completely — with Fit Realm leading your way.</span></p>
            <img src='images/about1.webp' alt='FitRealm - Best Fitness App' title='FitRealm - Best Fitness App' loading='lazy' />
          </div>
        </div>

        <div className='tablet-container' >
          <div className='features-container-mobile'>
            <h1>Features</h1>
            <div className='features-box-tablet'>
              <div onClick={() => navigate('/fit-g')}>
                <h2>Chat with <span style={{ color: '#FF3131' }}>Fit</span> G</h2>
                <p>Chat with our AI assistant, Fit G, to get started with your fitness journey.</p>
              </div>
              <img src='images/genie.webp' alt='Fitness Chatbot - Fit Realm' title='Chat with Fit G - Fit Realm' width="30" height="30" loading="lazy" />
            </div>
            <div className='features-box-tablet'>
              <div>
                <h2>AI Meal Planner</h2>
                <p>Chat with our AI assistant, Fit G, to get started with your fitness journey.</p>
              </div>
              <img src='images/diet.webp' alt='AI Diet Planner - Fit Realm' title='AI Diet Planner - Fit Realm' width="30" height="30" loading="lazy" />
            </div>
            <div className='features-box-tablet' onClick={() => navigate('/exerciselogs')}>
              <div>
                <h2>AI Exercise Planner</h2>
                <p>Chat with our AI assistant, Fit G, to get started with your fitness journey.</p>
              </div>
              <img src='images/exercise.webp' alt='AI Exercise Planner - Fit Realm' title='AI Exercise Planner - Fit Realm' width="30" height="30" loading="lazy" />
            </div>
            <div className='features-box-tablet' onClick={() => navigate('/calculators/bmi')}>
              <div>
                <h2><span style={{ color: '#FF3131' }}>Fit</span>ness Calculators</h2>
                <p>Chat with our AI assistant, Fit G, to get started with your fitness journey.</p>
              </div>
              <img src='images/calculator.webp' alt='Fitness Calculators - Fit Realm' title='Fitness Calculators' width="30" height="30" loading="lazy" />
            </div>
          </div>
        </div>

        <div className='tablet-container' >
          <div className='bottom-container-mobile'>
            <h1>OUR <span style={{ color: '#FF3131' }}>SOCIALS</span></h1>
            <hr style={{ width: '40%', border: '2px solid #FF3131' }} />
            <div className='socials-container-tablet'>
              <a href='https://www.instagram.com/the.fitrealm_/' target='_blank' rel='noopener noreferrer'><img src='images/instagram.webp' alt='Instagram - Fit Realm' title='Instagram - Fit Realm' width="30" height="30" loading="lazy" /></a>
              <a href='https://www.facebook.com/profile.php?id=61577838901247' target='_blank' rel='noopener noreferrer'><img src='images/facebook.webp' alt='Facebook - Fit Realm' title='Facebook - Fit Realm' width="30" height="30" loading="lazy" /></a>
              <a href='https://www.linkedin.com/company/fit-realm/' target='_blank' rel='noopener noreferrer'><img src='images/linkedin.webp' alt='Linkedin - Fit Realm' title='Linkedin - Fit Realm' width="30" height="30" loading="lazy" /></a>
              <a href='/blogs'><img src='images/notes.webp' alt='Blogs - Fit Realm' title='Blogs - Fit Realm' width="30" height="30" loading="lazy" /></a>
            </div>
          </div>
        </div>

      </div>
        )}
      </main>
    </div>
  );
}

export default App;